import { clsx } from 'clsx';
import { Component } from '@headless-workspace/typings';
import { GlowChip, GlowTextBody, GlowTextBodySmall } from '../../atoms';
import { GlowCTA } from '../cta/GlowCTA';
import { GlowSelectorImage } from '../selectorImage';
import { SelectorProps } from './SelectorProps';

/**
 * A component that renders a product selector.
 * @param titleContent - Contains the title of the selector and its font weight.
 * @param caption - Contains the caption of the selector text and its color.
 * @param chipWithText - Contains chip color and text.
 * @param RightElement - The element displayed on the right.
 * @param onClick - The function to be called when the selector is clicked.
 * @param fontFamily - Font family for selector texts.
 * @param props - Contains the following:
 * @param props.image - The image on the left.
 * @param props.imageClassName - Additional styles for the image.
 * @param props.shapeClassName - The style of the shape on the left.
 * @param props.isAvailable - Indicates if the product is available.
 */
export const GlowSelector: Component<SelectorProps> = ({
    titleContent,
    subTitleContent,
    chipWithText,
    RightElement,
    onClick,
    fontFamily = 'brand',
    ...props
}) => {
    const { isAvailable = true, image, imageClassName, shapeClassName } = props;
    const className =
        'flex justify-center items-center flex-1 w-full gap-0.5 px-0.75 py-0.5 bg-surface-primary outline outline-border-primary outline-medium rounded-0.5';
    const content = (
        <>
            {image ? (
                <div className={clsx('relative', imageClassName)}>
                    <GlowSelectorImage
                        image={image}
                        isAvailable={isAvailable}
                        shapeClassName={shapeClassName}
                        ImageTagName={props.ImageTagName}
                    />
                </div>
            ) : null}

            <span className={'flex flex-col flex-1 overflow-hidden'}>
                <span className={'flex flex-col items-start tablet:flex-row tablet:items-center tablet:gap-0.5'}>
                    <GlowTextBody
                        text={titleContent.label}
                        fontWeight={titleContent.fontWeight}
                        className={'text-left line-clamp-1 text-ellipsis break-all'}
                        fontFamily={fontFamily}
                        TagName={'span'}
                    />
                    {subTitleContent && (
                        <GlowTextBodySmall
                            text={subTitleContent.label}
                            color={subTitleContent.color}
                            fontFamily={fontFamily}
                            TagName={'span'}
                            className={'text-left line-clamp-1 text-ellipsis'}
                        />
                    )}
                </span>
                {chipWithText && (
                    <div className={'flex gap-0.5 items-center'}>
                        <GlowChip hasDigit={false} status={chipWithText.status} size={'medium'} />
                        <GlowTextBody text={chipWithText.label} fontFamily={fontFamily} TagName={'span'} />
                    </div>
                )}
            </span>

            {RightElement}
        </>
    );

    if (!onClick) {
        return <div className={className}>{content}</div>;
    }

    return (
        <GlowCTA onClick={onClick} className={className}>
            {content}
        </GlowCTA>
    );
};
